import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebaseConfig';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  User,
  Habit,
  Task,
  getUserProfile,
  createUserProfile,
  getHabits,
  getTasks,
  DEFAULT_CATEGORIES,
} from '../services/firebaseService';
import { subscribeToUserCompletions, Completion } from '../services/habitService';
import { dayKey } from '../utils/score';

// Types
interface AppState {
  user: User | null;
  habits: Habit[];
  tasks: Task[];
  completions: Record<string, Completion>;
  loading: boolean;
  error: string | null;
}

type AppAction = 
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_HABITS'; payload: Habit[] }
  | { type: 'SET_TASKS'; payload: Task[] }
  | { type: 'SET_COMPLETIONS'; payload: Record<string, Completion> }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'ADD_HABIT'; payload: Habit }
  | { type: 'UPDATE_HABIT'; payload: { id: string; updates: Partial<Habit> } }
  | { type: 'REMOVE_HABIT'; payload: string }
  | { type: 'ADD_TASK'; payload: Task }
  | { type: 'UPDATE_TASK'; payload: { id: string; updates: Partial<Task> } }
  | { type: 'REMOVE_TASK'; payload: string };

const initialState: AppState = {
  user: null,
  habits: [],
  tasks: [],
  completions: {},
  loading: true,
  error: null
};

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload };
    
    case 'SET_HABITS':
      return { ...state, habits: action.payload };
    
    case 'SET_TASKS':
      return { ...state, tasks: action.payload };
    
    case 'SET_COMPLETIONS':
      return { ...state, completions: action.payload };
    
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'ADD_HABIT':
      return { ...state, habits: [...state.habits, action.payload] };
    
    case 'UPDATE_HABIT':
      return {
        ...state,
        habits: state.habits.map(habit =>
          habit.id === action.payload.id
            ? { ...habit, ...action.payload.updates }
            : habit
        )
      };
    
    case 'REMOVE_HABIT':
      return {
        ...state,
        habits: state.habits.filter(habit => habit.id !== action.payload)
      };
    
    case 'ADD_TASK':
      return { ...state, tasks: [...state.tasks, action.payload] };
    
    case 'UPDATE_TASK':
      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === action.payload.id
            ? { ...task, ...action.payload.updates }
            : task
        )
      };
    
    case 'REMOVE_TASK':
      return {
        ...state,
        tasks: state.tasks.filter(task => task.id !== action.payload)
      };
    
    default:
      return state;
  }
};

// Context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Computed values
  todaysTasks: Task[];
  overdueTasks: Task[];
  completedTasksToday: Task[];
  activeHabits: Habit[];
  allCategories: string[];
  getTodayCompletions: () => string[];
  getHabitStreak: (habitId: string) => number;
  getTasksByCategory: (category: string) => Task[];
  getTasksByPriority: (priority: 'high' | 'medium' | 'low') => Task[];
}

// Exported so a screen can be mounted against a hand-built store -- see
// src/preview/. Nothing in the app should provide this except AppProvider.
export const AppContext = createContext<AppContextType | null>(null);

// Provider
export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [firebaseUser, loading] = useAuthState(auth);

  // Initialize user profile
  useEffect(() => {
    const initializeUser = async () => {
      if (loading) return;
      
      if (!firebaseUser) {
        dispatch({ type: 'SET_USER', payload: null });
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      try {
        let userProfile = await getUserProfile(firebaseUser.uid);
        
        // Create profile if it doesn't exist — only for genuinely new users
        // Double-check before creating to prevent overwriting existing profiles
        if (!userProfile) {
          const doubleCheck = await getUserProfile(firebaseUser.uid);
          if (doubleCheck) {
            userProfile = doubleCheck;
          } else {
            await createUserProfile({
              uid: firebaseUser.uid,
              email: firebaseUser.email!,
              displayName: firebaseUser.displayName || 'User'
            });
            userProfile = await getUserProfile(firebaseUser.uid);
          }
        }

        dispatch({ type: 'SET_USER', payload: userProfile });

        // Update lastActiveAt for admin analytics
        try {
          await updateDoc(doc(db, 'users', firebaseUser.uid), { lastActiveAt: serverTimestamp() });
        } catch (_) { /* non-critical */ }
      } catch (error) {
        console.error('Error initializing user:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load user profile' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeUser();
  }, [firebaseUser, loading]);

  // Subscribe to habits
  useEffect(() => {
    if (!state.user) return;

    const unsubscribe = getHabits(state.user.uid, (habits) => {
      dispatch({ type: 'SET_HABITS', payload: habits });
    });

    return unsubscribe;
  }, [state.user]);

  // Subscribe to tasks
  useEffect(() => {
    if (!state.user) {
      console.log('❌ No user found, cannot subscribe to tasks');
      return;
    }

    console.log('🔑 Current user:', {
      uid: state.user.uid,
      email: state.user.email,
      displayName: state.user.displayName
    });

    const unsubscribe = getTasks(state.user.uid, (tasks) => {
      console.log('📋 Tasks received in context:', tasks.length);
      dispatch({ type: 'SET_TASKS', payload: tasks });
    });

    return unsubscribe;
  }, [state.user]);

  // Subscribe to completions
  useEffect(() => {
    if (!state.user) return;

    const unsubscribe = subscribeToUserCompletions(state.user.uid, (completions) => {
      dispatch({ type: 'SET_COMPLETIONS', payload: completions });
    });

    return unsubscribe;
  }, [state.user]);

  // Computed values
  const todaysTasks = state.tasks.filter(task => {
    if (task.completed) return false;
    if (!task.deadline) return false;
    
    const today = dayKey(new Date());
    const taskDate = dayKey(new Date(task.deadline));
    return taskDate === today;
  });

  const overdueTasks = state.tasks.filter(task => {
    if (task.completed) return false;
    if (!task.deadline) return false;
    
    const today = new Date();
    const taskDate = new Date(task.deadline);
    return taskDate < today;
  });

  const completedTasksToday = state.tasks.filter(task => {
    if (!task.completed || !task.completedAt) return false;
    
    const today = dayKey(new Date());
    const completedDate = dayKey(new Date(task.completedAt.toDate()));
    return completedDate === today;
  });

  const activeHabits = state.habits.filter(habit => !habit.userId || habit.userId === state.user?.uid);

  const getTodayCompletions = (): string[] => {
    const today = dayKey(new Date());
    return state.completions[today]?.habits || [];
  };

  const getHabitStreak = (habitId: string): number => {
    const habit = state.habits.find(h => h.id === habitId);
    return habit?.streak || 0;
  };

  const getTasksByCategory = (category: string): Task[] => {
    return state.tasks.filter(task => task.category === category && !task.completed);
  };

  const getTasksByPriority = (priority: 'high' | 'medium' | 'low'): Task[] => {
    const priorityColors = {
      high: '#FF3B30',
      medium: '#FF9500',
      low: '#34C759'
    };
    
    return state.tasks.filter(task => 
      task.color === priorityColors[priority] && !task.completed
    );
  };

  // Centralized categories: defaults (minus hidden) + profile customs + task-derived
  const allCategories = useMemo(() => {
    const hiddenDefaults = state.user?.hiddenDefaultCategories || [];
    const profileCustoms = state.user?.customCategories?.map((c) => c.name) || [];
    const cats = new Set([
      ...DEFAULT_CATEGORIES.filter((c) => !hiddenDefaults.includes(c)),
      ...profileCustoms,
    ]);
    // Also include categories found on existing tasks
    state.tasks.forEach((t) => {
      if (t.category && !hiddenDefaults.includes(t.category)) {
        cats.add(t.category);
      }
    });
    return Array.from(cats);
  }, [state.user, state.tasks]);

  const contextValue: AppContextType = {
    state,
    dispatch,
    todaysTasks,
    overdueTasks,
    completedTasksToday,
    activeHabits,
    allCategories,
    getTodayCompletions,
    getHabitStreak,
    getTasksByCategory,
    getTasksByPriority
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;